import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Search, FileEdit, MessageSquare, Award, Receipt, Download, CheckCircle2, Star, ChevronRight, ExternalLink } from 'lucide-react';

// Public Supplier Demo Journey — visual workflow walkthrough.
// Per spec: 8 high-level stages. HIDE Smart Bid Wizard internals — show only "structured bidding" outcome.

const STAGES = [
  { key: 'discovery', label: 'Tender Discovery', icon: Search, blurb: 'Browse live tenders posted by Housing Societies and Facility Management firms. Filter by category, location, value.', highlight: 'Mobile-first marketplace.' },
  { key: 'bid', label: 'Bid Submission', icon: FileEdit, blurb: 'Submit a structured bid with pricing, scope confirmation and attachments. No Excel files required.', highlight: 'Standardised bid format.' },
  { key: 'clarification', label: 'Clarification', icon: MessageSquare, blurb: 'Raise questions about the tender. Buyer responds inside the platform — every exchange is timestamped.', highlight: 'Full audit trail.' },
  { key: 'award', label: 'Award', icon: Award, blurb: 'If your bid is selected, you get an instant award notification.', highlight: 'Mobile + portal alerts.' },
  { key: 'fee', label: 'Success Fee', icon: Receipt, blurb: 'A small platform success fee is auto-generated. Pay securely to unlock the Purchase Order.', highlight: '0.30% + GST · transparent.' },
  { key: 'po', label: 'PO Download', icon: Download, blurb: 'Once paid, download the digital Purchase Order with all contract terms.', highlight: 'PDF + audit log.' },
  { key: 'completion', label: 'Work Completion', icon: CheckCircle2, blurb: 'Confirm work completion, upload before/after evidence, acknowledge NOC and final payment.', highlight: 'Step-by-step closure.' },
  { key: 'feedback', label: 'Feedback', icon: Star, blurb: 'Rate the buyer · receive a rating from the buyer. Ratings appear on your public profile.', highlight: 'Bilateral trust building.' },
];

export default function SupplierDemoJourney() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const goPortal = (source) => {
    const isJourneyComplete = source === 'supplier_journey_completed' || source === 'supplier_journey_footer';
    const dest = isJourneyComplete
      ? `/register?role=supplier&source=${source}`
      : `/login?source=${source}`;
    navigate(dest);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white sticky top-0 z-30">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between" data-testid="public-site-nav">
          <button onClick={() => navigate('/')} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="public-nav-home-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Home
          </button>
          <div className="font-bold text-sm sm:text-base">Supplier Demo · Vendor Journey</div>
          <button onClick={() => goPortal('supplier_demo_top')} className="px-3 py-1.5 bg-gold text-navy font-bold text-xs rounded inline-flex items-center" data-testid="public-nav-access-portal-btn">
            Access Portal <ExternalLink className="h-3 w-3 ml-1" />
          </button>
        </nav>
      </header>

      <section className="bg-gradient-to-b from-navy to-navy/90 text-white py-10 sm:py-14" data-testid="supplier-journey-hero">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="inline-block px-3 py-1 rounded-full border border-gold/40 text-gold text-[10px] uppercase tracking-wider font-bold mb-3">For Housekeeping · Security · Lift AMC · CCTV · Pest Control · Plumbing · Electrical · and more</div>
          <h1 className="text-3xl sm:text-5xl font-bold mb-3">Win More Contracts — in 8 Stages</h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto">Click each stage to see how ProcureOne Ultra helps small and mid-size service vendors find work, bid confidently and get paid — even with just a smartphone.</p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-8 max-w-5xl" data-testid="supplier-journey-stage-list">
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-8 overflow-x-auto">
          {STAGES.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === active;
            const isDone = i < active;
            return (
              <button key={s.key} onClick={() => setActive(i)} className="flex flex-col items-center group" data-testid={`supplier-stage-${s.key}`}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${isActive ? 'bg-orange border-orange text-white scale-110 shadow-lg' : isDone ? 'bg-gold border-gold text-navy' : 'bg-card border-border text-muted-foreground'}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className={`text-[10px] font-bold mt-1 text-center leading-tight ${isActive ? 'text-orange' : isDone ? 'text-gold' : 'text-muted-foreground'}`}>
                  {s.label}
                </div>
              </button>
            );
          })}
        </div>

        <div className="bg-card border-2 border-orange/30 rounded-2xl p-6 sm:p-8" data-testid="supplier-stage-detail">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-orange/10 flex items-center justify-center flex-shrink-0">
              {(() => { const Icon = STAGES[active].icon; return <Icon className="h-7 w-7 text-orange" />; })()}
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Stage {active + 1} of 8</div>
              <h2 className="text-xl sm:text-2xl font-bold text-navy mt-0.5" data-testid="supplier-stage-detail-title">{STAGES[active].label}</h2>
              <p className="text-sm text-foreground/80 mt-2 leading-relaxed" data-testid="supplier-stage-detail-blurb">{STAGES[active].blurb}</p>
              <div className="mt-3 inline-flex items-center px-3 py-1.5 rounded-full bg-gold/15 text-navy text-xs font-bold" data-testid="supplier-stage-detail-highlight">
                ✦ {STAGES[active].highlight}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl bg-gradient-to-br from-navy via-navy/95 to-navy/80 p-6 sm:p-8 text-white" data-testid="supplier-stage-screenshot-mock">
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/10">
              <div className="flex gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /><span className="w-2.5 h-2.5 rounded-full bg-green-400" /></div>
              <div className="text-xs font-mono text-white/60">portal.procureoneultra.com — {STAGES[active].label}</div>
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-white/15 rounded w-3/4" />
              <div className="h-3 bg-white/10 rounded w-full" />
              <div className="h-3 bg-white/10 rounded w-5/6" />
              <div className="flex gap-2 mt-4">
                <div className="h-9 w-32 bg-gold/30 rounded flex items-center justify-center text-[10px] font-bold text-gold">{active === 1 ? 'Submit Bid' : active === 4 ? 'Pay Fee' : active === 5 ? 'Download PO' : 'Continue'}</div>
                <div className="h-9 w-24 bg-white/10 rounded" />
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-6">
            <button onClick={() => setActive(Math.max(0, active - 1))} disabled={active === 0} className="px-3 py-2 rounded text-sm font-bold inline-flex items-center disabled:opacity-30" data-testid="supplier-stage-prev-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Previous
            </button>
            <span className="text-xs text-muted-foreground">{active + 1} / 8</span>
            {active < STAGES.length - 1 ? (
              <button onClick={() => setActive(active + 1)} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold inline-flex items-center" data-testid="supplier-stage-next-btn">
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            ) : (
              <button onClick={() => goPortal('supplier_journey_completed')} className="px-4 py-2 rounded bg-orange text-white text-sm font-bold inline-flex items-center" data-testid="supplier-stage-cta-btn">
                Start Winning Contracts <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-12" data-testid="supplier-journey-footer-cta">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h3 className="text-2xl sm:text-3xl font-bold mb-2">Reach Housing Societies and Facility Managers near you.</h3>
          <p className="text-white/70 mb-6">Founding Member registrations are open. Subscriptions open. Early access available.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/demo/buyer-journey')} className="px-6 py-3 rounded-lg border-2 border-white/30 text-white text-sm font-bold inline-flex items-center justify-center" data-testid="supplier-journey-buyer-link-btn">
              See Buyer Journey <ArrowRight className="h-4 w-4 ml-2" />
            </button>
            <button onClick={() => goPortal('supplier_journey_footer')} className="px-6 py-3 rounded-lg bg-gold text-navy text-sm font-bold inline-flex items-center justify-center" data-testid="supplier-journey-access-portal-btn">
              Access Portal <ExternalLink className="h-4 w-4 ml-2" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
